import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../store/authSlice';
import {
  Monitor, Shield, Wifi, Activity, Eye, EyeOff,
  ArrowRight, AlertCircle, CheckCircle2,
} from 'lucide-react';
import NetworkCanvas from './NetworkCanvas';
import './Login.css';

function StatCard({ icon, label, value, sub, style, className }) {
  return (
    <div className={`stat-card ${className || ''}`} style={{ minWidth: 140, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ color: 'var(--accent-primary)', opacity: .85 }}>{icon}</div>
        <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading: reduxLoading, error: reduxError, token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      navigate('/dashboard');
    }
  }, [token, navigate]);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [shakeCard, setShakeCard] = useState(false);

  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const cx = (e.clientX - r.left - r.width / 2) / r.width;
    const cy = (e.clientY - r.top - r.height / 2) / r.height;
    setTilt({ x: cy * 3.5, y: -cx * 3.5 });
  }, []);

  const handleMouseLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  const handleCapsLock = (e) => setCapsLock(e.getModifierState('CapsLock'));

  const addRipple = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const s = Math.max(r.width, r.height) * 2;
    const span = document.createElement('span');
    span.className = 'btn-ripple';
    span.style.cssText = `width:${s}px;height:${s}px;left:${e.clientX - r.left - s / 2}px;top:${e.clientY - r.top - s / 2}px;`;
    btn.appendChild(span);
    setTimeout(() => span.remove(), 600);
  };

  const handleSubmit = async (e) => {
    addRipple(e);
    setLocalError('');
    if (!username || !password) {
      setLocalError('Please fill in all fields.');
      setShakeCard(true);
      setTimeout(() => setShakeCard(false), 500);
      return;
    }

    try {
      const resultAction = await dispatch(loginUser({ username, password }));
      if (loginUser.fulfilled.match(resultAction)) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        setShakeCard(true);
        setTimeout(() => setShakeCard(false), 500);
      }
    } catch (err) {
      setLocalError('An unexpected error occurred');
      setShakeCard(true);
      setTimeout(() => setShakeCard(false), 500);
    }
  };

  return (
    <div className="auth-container">
      {/* ── Left panel ── */}
      <div className="auth-left">
        <NetworkCanvas />

        {/* Grid fade to right */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent 70%, var(--bg-main))', pointerEvents: 'none' }} />
        {/* Bottom vignette */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 60%, var(--bg-main))', pointerEvents: 'none' }} />

        {/* Top brand bar */}
        <div style={{ position: 'absolute', top: 28, left: 32, right: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'fadeUp 500ms both 300ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Monitor size={16} color="#fff" strokeWidth={2} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.01em' }}>DYNAMIC 360</span>
          </div>
          <div className="status-pill">
            <span className="anim-blink" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-primary)', display: 'inline-block' }} />
            LIVE
          </div>
        </div>

        {/* Floating stat cards */}
        <div style={{ position: 'absolute', top: '14%', left: 28, animation: 'fadeUp 600ms both 600ms' }}>
          <StatCard icon={<Monitor size={14} />} label="Total Devices" value="250" sub="Registered fleet" className="anim-float" />
        </div>
        <div style={{ position: 'absolute', top: '28%', right: '16%', animation: 'fadeUp 600ms both 750ms' }}>
          <StatCard icon={<Wifi size={14} />} label="Online" value="128" sub="Active endpoints" className="anim-float-alt" />
        </div>
        <div style={{ position: 'absolute', bottom: '28%', left: 28, animation: 'fadeUp 600ms both 900ms' }}>
          <StatCard icon={<Activity size={14} />} label="Offline" value="42" sub="Disconnected" className="anim-float" style={{ animationDelay: '1s' }} />
        </div>
        <div style={{ position: 'absolute', bottom: '14%', right: '14%', animation: 'fadeUp 600ms both 1050ms' }}>
          <StatCard icon={<Shield size={14} />} label="Idle" value="80" sub="Inactive endpoints" className="anim-float-alt" style={{ animationDelay: '.5s' }} />
        </div>

        {/* Bottom tagline */}
        <div style={{ position: 'absolute', bottom: 28, left: 32, animation: 'fadeUp 600ms both 1100ms' }}>
          <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.02em', lineHeight: 1.3 }}>
            Every device.<br />
            <span style={{ color: 'var(--accent-primary)' }}>Always visible.</span>
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>
            Real-time visibility across your entire organization.
          </p>
        </div>
      </div>

      {/* ── Right panel (Login form) ── */}
      <div className="auth-right">
        {/* Glow behind card */}
        <div className="ambient anim-breathe" style={{ position: 'absolute', width: 320, height: 320, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'radial-gradient(circle, rgba(var(--app-primary-color-rgb), 0.1) 0%, transparent 70%)', zIndex: 0 }} />

        {/* Card */}
        <div
          ref={cardRef}
          className="nm-card"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            maxWidth: 400,
            padding: '32px',
            position: 'relative',
            zIndex: 1,
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${shakeCard ? 0.98 : 1})`,
            transition: shakeCard ? 'transform 0.1s' : 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform',
          }}
        >
          {/* Card edge highlight */}
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,var(--accent-primary),transparent)' }} />

          {/* ── Logo ── */}
          <div style={{ marginBottom: 28, animation: 'logoEntrance 600ms cubic-bezier(0.34,1.56,0.64,1) both 500ms' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                height: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '6px 14px', borderRadius: 12, background: '#FFFFFF',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              }}>
                <img src={require('../assets/Dynamic_Logo.png')} alt="Dynamic Agent Logo" style={{ maxHeight: 36, maxWidth: 160, objectFit: 'contain' }} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-.01em', color: 'var(--text-primary)' }}>DYNAMIC AGENT</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginTop: 1 }}>Monitor Every Device Securely</div>
              </div>
            </div>
          </div>

          {/* ── Heading ── */}
          <div style={{ marginBottom: 6, animation: 'fadeUp 500ms both 600ms' }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.025em', lineHeight: 1.2 }}>
              Welcome back
            </h1>
          </div>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: 'var(--text-secondary)', animation: 'fadeUp 500ms both 650ms' }}>
            Sign in to your organization dashboard.
          </p>

          {/* ── Error banner ── */}
          {(localError || reduxError) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.25)', marginBottom: 16, animation: 'fadeUp 300ms both' }}>
              <AlertCircle size={14} color="#EF4444" />
              <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 500 }}>{localError || reduxError}</span>
            </div>
          )}

          {/* ── Success ── */}
          {success && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)', marginBottom: 16, animation: 'fadeUp 300ms both' }}>
              <CheckCircle2 size={14} color="#22C55E" />
              <span style={{ fontSize: 12, color: '#22C55E', fontWeight: 500 }}>Authentication successful. Redirecting…</span>
            </div>
          )}

          {/* ── Fields ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Username */}
            <div className="nm-field" style={{ animation: 'fadeUp 500ms both 700ms' }}>
              <input
                type="text"
                placeholder=" "
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                id="lg-username"
                autoComplete="username"
              />
              <label htmlFor="lg-username">Username or Email</label>
            </div>

            {/* Password */}
            <div className="nm-field" style={{ animation: 'fadeUp 500ms both 760ms', position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={handleCapsLock}
                id="lg-pass"
                autoComplete="current-password"
                style={{ paddingRight: 44 }}
              />
              <label htmlFor="lg-pass">Password</label>
              <button
                onClick={(e) => { e.preventDefault(); setShowPass(v => !v); }}
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', transition: 'color 300ms', padding: 0, display: 'flex' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent-primary)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                tabIndex={-1}
              >
                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>

            {/* Caps Lock warning */}
            {capsLock && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, animation: 'fadeUp 200ms both' }}>
                <AlertCircle size={12} color="var(--accent-primary)" />
                <span style={{ fontSize: 11, color: 'var(--accent-primary)', fontWeight: 500 }}>Caps Lock is on</span>
              </div>
            )}
          </div>

          {/* ── Remember / Forgot ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 22, animation: 'fadeUp 500ms both 820ms' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="nm-check"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
              />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 400 }}>Remember me</span>
            </label>
            <button className="nm-link" onClick={(e) => { e.preventDefault(); navigate('/forgot'); }}>
              Forgot password?
            </button>
          </div>

          {/* ── Sign in button ── */}
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={reduxLoading || success}
            style={{ width: '100%', height: 48, fontSize: 14, fontWeight: 600, animation: 'fadeUp 500ms both 870ms' }}
          >
            {reduxLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spinFast .7s linear infinite' }} />
                Authenticating…
              </div>
            ) : success ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><CheckCircle2 size={16} /> Signed in</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>Sign In <ArrowRight size={15} /></div>
            )}
          </button>

          {/* ── Divider ── */}
          <div className="divider" style={{ margin: '20px 0', animation: 'fadeUp 500ms both 920ms', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-light)' }}></div>
            <span style={{ padding: '0 10px' }}>or</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-light)' }}></div>
          </div>

          {/* ── Register link ── */}
          <p style={{ textAlign: 'center', margin: '20px 0 0', fontSize: 13, color: 'var(--text-muted)', animation: 'fadeUp 500ms both 1020ms' }}>
            New to Dynamic 360?{' '}
            <button className="nm-link" onClick={(e) => { e.preventDefault(); navigate('/register'); }}>
              Create organization
            </button>
          </p>

          {/* Card edge highlight bottom */}
          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,var(--accent-primary),transparent)' }} />
        </div>

        {/* Footer */}
        <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 20, animation: 'fadeIn 600ms both 1100ms' }}>
          {['Privacy', 'Terms', 'Help Center'].map(l => (
            <button key={l} className="nm-link" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l}</button>
          ))}
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>v0.0.1</span>
        </div>
      </div>
    </div>
  );
}
